import { z } from 'zod';

/** Каналы с зарезервированными адаптерами (реализация — поэтапно). */
export const MessagingChannelKind = z.enum([
  'TELEGRAM_MTPROTO',
  'INSTAGRAM_META',
  'WHATSAPP_CLOUD',
  'FACEBOOK_MESSENGER',
  'VK_MESSAGES',
  'WEB_CHAT',
]);
export type MessagingChannelKind = z.infer<typeof MessagingChannelKind>;

export const MessagingAdapterCapabilities = z.object({
  inbound: z.boolean(),
  outbound: z.boolean(),
  deliveryReceipts: z.boolean(),
  readReceipts: z.boolean(),
  attachments: z.boolean(),
  historySync: z.boolean(),
  /** Краткое описание ограничений для UI (напр. окно 24h Meta). */
  notes: z.string().max(500).optional(),
});
export type MessagingAdapterCapabilities = z.infer<typeof MessagingAdapterCapabilities>;

export const NormalizedInboundMessage = z.object({
  channelAccountId: z.uuid(),
  externalThreadId: z.string().min(1).max(128),
  externalMessageId: z.string().min(1).max(128),
  externalParticipantId: z.string().min(1).max(128),
  participantLabel: z.string().max(120).optional(),
  cityId: z.uuid().optional(),
  direction: z.enum(['INBOUND', 'OUTBOUND', 'SYSTEM']),
  senderRole: z.enum(['CUSTOMER', 'MANAGER', 'SYSTEM']),
  body: z.string().max(20_000).optional(),
  attachments: z.array(z.record(z.string(), z.unknown())).max(20).optional(),
  occurredAt: z.iso.datetime(),
  status: z.enum(['RECEIVED', 'QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED']).optional(),
});
export type NormalizedInboundMessage = z.infer<typeof NormalizedInboundMessage>;

export const OutboundMessagePayload = z.object({
  conversationId: z.uuid(),
  body: z.string().min(1).max(20_000),
  attachmentIds: z.array(z.string().max(128)).max(10).optional(),
});
export type OutboundMessagePayload = z.infer<typeof OutboundMessagePayload>;

export type MessagingAdapterHealth = {
  ok: boolean;
  detail?: string;
};

/**
 * Контракт адаптера мессенджера. Реализации живут в apps/worker-* или apps/api,
 * регистрируются в MessengerAdapterRegistry.
 */
export interface MessengerAdapter {
  readonly channel: MessagingChannelKind;
  readonly capabilities: MessagingAdapterCapabilities;

  connect(channelAccountId: string): Promise<void>;
  disconnect(channelAccountId: string): Promise<void>;
  healthCheck(channelAccountId: string): Promise<MessagingAdapterHealth>;

  syncConversations?(
    channelAccountId: string,
    cursor?: string,
  ): Promise<{ cursor?: string; imported: number }>;

  syncMessages?(conversationId: string, cursor?: string): Promise<{ cursor?: string; imported: number }>;

  sendMessage?(
    channelAccountId: string,
    payload: OutboundMessagePayload,
  ): Promise<{ messageId: string; status: 'QUEUED' | 'SENT' | 'FAILED' }>;

  /** Поставить уже созданное QUEUED-сообщение в очередь отправки. */
  enqueueOutbound?(messageId: string): Promise<void>;
}
